/**
 * Where a record applies — `docs/services-and-branch-scope.md` §3.
 *
 * Aaron: "There needs to an option to assign this to branches … I want to
 * build this out as a standardized branch association that we can use in a
 * number of different places. The default should be all branches. The branch
 * selector should look a lot like the branch drop down in the header for the
 * whole app. Frankly, it should practically share the same code, but
 * obviously have a different outcome."
 *
 * So it does share the code: `scope/scope-tree.tsx` draws the rows for both,
 * and `shared/src/scope.ts` is the one cascade under both — ticking a region
 * ticks every branch beneath it because `toggle()` already says so, not
 * because this file repeats the rule. What differs is the outcome. The header
 * is asking *what am I looking at*, so it keeps recents, "only", a keyboard
 * and an apply-on-close. A field is asking *where does this apply*, so it
 * keeps none of them and answers to a form instead.
 *
 * **Empty is everywhere.** Not nothing: a service offered at every branch is
 * the common case and the default, so it stores no rows, and the trigger says
 * "Everywhere" rather than counting to zero. Clearing the selection is how
 * you go back to that, which is why there is a Clear and no "all" tick box —
 * a tick box for the default would be a second way to say the same thing, and
 * the two would disagree the first time somebody added a branch.
 */
import { useMemo, useRef, useState } from 'react'
import {
  coveredBranches, normalise, scopeLabelOf, toggle, type OrgUnitSummary,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Popover } from '../overlays.js'
import { Button } from '../primitives.js'
import { IconChevronDown, IconPin, IconSearch } from '../../frame/icons.js'
import {
  NoMatch, ScopeRow, defaultOpen, offerable, plural, rowsOf, treeOf,
} from '../../scope/scope-tree.js'

export interface BranchScopeProps {
  /** The org units this person can see — `me.scope.available`, as the header gets it. */
  available: OrgUnitSummary[]
  /** What was ticked: roots, not the branches under them. Empty is everywhere. */
  value: string[]
  onChange: (roots: string[]) => void
  /** The form control's id, so a `Field` label points at it. */
  id?: string
  disabled?: boolean
  /** Opened over a modal — the service pop-out. See `Popover`'s `layer`. */
  layer?: number
}

/**
 * What the closed trigger says.
 *
 * `scopeLabelOf` is the header's sentence-free naming — it names places, never
 * a count of them — so a field and the header describe a selection the same
 * way. The one thing it does not have a word for is the empty selection,
 * because the header can never be empty: no roots means the server falls back
 * to everything. Here empty is a real, chosen state, and it is the default.
 */
export function branchScopeLabel(roots: string[], available: OrgUnitSummary[]): string {
  // "Everywhere" — the catalog boards' word for it (cb-01, cb-04, cb-18).
  if (roots.length === 0) return EVERYWHERE
  return scopeLabelOf(roots, treeOf(available)).text
}

const EVERYWHERE = 'Everywhere'

export function BranchScopeField({
  available, value, onChange, id, disabled, layer,
}: BranchScopeProps) {
  const anchor = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [showClosed, setShowClosed] = useState(false)

  const units = useMemo(
    () => offerable(available, showClosed), [available, showClosed])
  const tree = useMemo(() => treeOf(units), [units])
  const facts = useMemo(
    () => new Map(units.map((unit) => [unit.id, unit])), [units])

  /*
   * Normalised against the tree the *picker* is showing, so a root that is
   * not offerable — a region whose only branch closed — still counts. Its
   * rows are hidden, not its meaning.
   */
  const roots = useMemo(() => normalise(value, treeOf(available)), [value, available])

  const [opened, setOpened] = useState<Set<string> | null>(null)
  const openIds = opened ?? defaultOpen(roots, tree)
  const rows = useMemo(
    () => rowsOf(tree, facts, openIds, query), [tree, facts, openIds, query])

  const closedCount = available.filter(
    (unit) => unit.kind === 'branch' && unit.reachable && unit.closed).length

  /* What the selection actually reaches, which is the number worth printing. */
  const covered = useMemo(
    () => coveredBranches(roots, treeOf(available)).length, [roots, available])

  return (
    <div ref={anchor} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-control border px-2.5',
          'bg-surface text-body text-text disabled:opacity-50',
          open ? 'border-accent-line ring-2 ring-accent-ring' : 'border-line hover:bg-hover')}
      >
        {roots.length === 0
          ? (
            /* The default reads as a chip, not as a place (cb-04 · Where it is offered). */
            <span className="flex min-w-0 flex-1">
              <span className="inline-flex h-6 items-center rounded-button border border-accent-line bg-accent-soft px-[9px] text-[12px] font-semibold text-accent-dark">
                {EVERYWHERE}
              </span>
            </span>
          )
          : (
            <>
              <span aria-hidden className="shrink-0 text-text-faint"><IconPin size={14} /></span>
              <span className="min-w-0 flex-1 truncate text-left">
                {branchScopeLabel(roots, available)}
              </span>
            </>
          )}
        {roots.length > 0 && (
          <span className="shrink-0 text-chip tabular-nums text-text-faint">
            {plural(covered, 'branch')}
          </span>
        )}
        <span aria-hidden className="shrink-0 text-text-faint"><IconChevronDown size={13} /></span>
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        label="Where it is offered"
        width={340}
        anchor={anchor.current}
        className="rounded-popup"
        layer={layer}
      >
        <div className="p-2 pb-1">
          <label className={cn('flex h-[34px] items-center gap-2 rounded-control border',
            'border-line bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent-ring')}
          >
            <span aria-hidden className="shrink-0 text-text-muted"><IconSearch size={14} /></span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Find a place"
              placeholder="Find a place…"
              className={cn('w-full min-w-0 bg-transparent text-cell text-text',
                'placeholder:text-text-faint focus:outline-none focus-visible:shadow-none')}
            />
          </label>
        </div>

        <div role="listbox" aria-label="Places" className="max-h-[320px] overflow-y-auto pb-1">
          {rows.length === 0 && <NoMatch query={query} />}
          {rows.map((row) => (
            <ScopeRow
              key={row.unit.id}
              row={row}
              tree={tree}
              roots={roots}
              onToggle={() => onChange(toggle(row.unit.id, roots, tree))}
              onToggleOpen={() => setOpened(() => {
                const next = new Set(openIds)
                if (next.has(row.unit.id)) next.delete(row.unit.id)
                else next.add(row.unit.id)
                return next
              })}
            />
          ))}

          {closedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowClosed((current) => !current)}
              className={cn('mx-1.5 mt-0.5 flex h-8 items-center gap-1.5 rounded-control',
                'pl-[10px] pr-2.5 text-chip font-semibold text-text-muted hover:bg-hover')}
            >
              {showClosed ? 'Hide' : 'Show'} {plural(closedCount, 'closed branch')}
            </button>
          )}
        </div>

        {/*
          * The footer says what the selection means, in branches, because
          * "East Coast" is a place and "14 branches" is the consequence. And
          * Clear is how you get back to everywhere: there is no "all" tick,
          * for the reason at the top of this file.
          */}
        <footer className={cn('flex items-center gap-2 border-t border-line',
          'bg-surface-alt px-3 py-2')}
        >
          <span className="text-sub text-text-muted">
            {roots.length === 0
              ? 'Offered at every branch'
              : `Offered at ${plural(covered, 'branch')}`}
          </span>
          <span className="ml-auto flex items-center gap-2">
            {/*
              * "Offer everywhere", not "All branches".
              *
              * The trigger already says "Everywhere" when nothing is
              * ticked, and a *button* with the same words two inches away is
              * a state and an action wearing one label — which is also how a
              * probe of this panel came to read the wrong element. The verb
              * says it is the thing that does it.
              */}
            {roots.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-cell font-semibold text-accent hover:text-accent-dark"
              >
                Offer everywhere
              </button>
            )}
            <Button variant="primary" size="toolbar" onClick={() => setOpen(false)}>
              Done
            </Button>
          </span>
        </footer>
      </Popover>
    </div>
  )
}
